import React, { useMemo, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { get } from 'lodash';
import { unstable } from '@broadleaf/admin-components';

const { Portal } = unstable;

export interface ReadOnlyBannerTriggerProps {
  state?: {
    data?: {
      contextState?: {
        mutable?: boolean;
      };
    };
  };
}

// List of routes that are permanently read-only
const PERMANENTLY_READ_ONLY_ROUTES = ['/dealer-products'];

export const ReadOnlyBannerTrigger: React.FC<ReadOnlyBannerTriggerProps> = ({
  state
}) => {
  const location = useLocation();
  const intl = useIntl();

  // Normalize path to lowercase to resolve URL case-sensitivity
  const normalizedPath = location.pathname.toLowerCase();

  const isDealerProduct = useMemo(() => {
    return PERMANENTLY_READ_ONLY_ROUTES.some(route => {
      const routeLower = route.toLowerCase();
      return (
        normalizedPath === routeLower ||
        normalizedPath.startsWith(`${routeLower}/`)
      );
    });
  }, [normalizedPath]);

  const currentMutable = get(state, 'data.contextState.mutable');
  const isReadOnly = isDealerProduct || currentMutable === false;

  const [targetExists, setTargetExists] = useState(false);

  useEffect(() => {
    const hasTarget = !!document.getElementById('read-only-banner-target');
    setTargetExists(hasTarget);
  }, [location.pathname, isReadOnly]);

  if (!isReadOnly || !targetExists) {
    return null;
  }

  return (
    <Portal target="read-only-banner-target" element="div">
      <div
        role="status"
        aria-live="polite"
        className="tw-flex tw-w-full tw-items-center tw-justify-center tw-bg-yellow-500 tw-px-4 tw-py-1.5 tw-text-center tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-gray-900 tw-shadow-inner"
      >
        <span className="tw-mr-2" role="img" aria-label="warning">
          ⚠️
        </span>
        <span>
          {intl.formatMessage({
            id: 'ReadOnlyBanner.warning',
            defaultMessage:
              'READ ONLY - You have read-only access to this screen'
          })}
        </span>
      </div>
    </Portal>
  );
};

export default ReadOnlyBannerTrigger;
